import { createRouter, createWebHistory } from '@ionic/vue-router';
import { RouteRecordRaw } from 'vue-router';
import MainPage from '../views/MainPage.vue';
import AuthPage from '@/views/auth/AuthPage.vue';
import { useUserStore } from '@/store/user';
import LockedScreen from '@/views/LockedScreen.vue';
import { useAzureAuth } from '@/composeables/useAzureAuth';
import { hiddenSectionRedirect, useFeatureVisibility } from '@/composeables/useFeatureVisibility';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/auth/',
    component: AuthPage,
    children: [
      {
        path: '',
        redirect: '/auth/onboarding',
      },
      {
        path: 'onboarding',
        name: 'onboarding',
        component: () => import('@/views/auth/Onboarding.vue'),
      },
      {
        path: 'sign',
        name: 'sign',
        component: () => import('@/views/auth/SignPage.vue'),
      },
      {
        path: 'reset',
        name: 'reset-password',
        component: () => import('@/views/auth/ResetPasswordPage.vue'),
      },
    ],
  },
  {
    path: '/locked/',
    component: LockedScreen,
    name: 'locked-page',
  },
  {
    path: '/',
    component: MainPage,
    children: [
      {
        path: '',
        redirect: '/dashboard',
      },

      // DASHBOARD
      {
        path: 'dashboard',
        name: 'dashboard',
        alias: ['/anmalningar/mina'],
        component: () => import('@/views/Dashboard.vue'),
      },
      {
        path: 'dashboard/profile',
        name: 'profile',
        meta: { requiresAuth: true },
        component: () => import('@/views/profile/Profile.vue'),
      },
      {
        path: 'dashboard/user',
        name: 'user',
        component: () => import('@/views/profile/UserSettings.vue'),
      },
      {
        path: 'dashboard/company',
        name: 'company',
        component: () => import('@/views/profile/CompanySettings.vue'),
      },
      {
        path: 'dashboard/notifications',
        name: 'notifications',
        component: () => import('@/views/profile/NotificationSettings.vue'),
      },
      {
        path: 'dashboard/ice',
        name: 'ice',
        component: () => import('@/views/profile/IceSettings.vue'),
      },

      // GROUPS
      {
        path: 'groups',
        name: 'groups',
        component: () => import('@/views/Groups.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'showGroup',
        path: 'groups/show/:id',
        component: () => import('@/views/groups/ShowGroup.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'showGroupComments',
        path: 'groups/show/comments/:id',
        component: () => import('@/views/groups/ShowGroupComments.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'handleGroupMembers',
        path: 'groups/show/members/:id',
        component: () => import('@/views/groups/ShowGroupMembers.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'addGroupEmergency',
        path: 'groups/addEmergency/:id',
        component: () => import('@/views/groups/AddEmergency.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'editGroup',
        path: 'groups/edit/:id',
        component: () => import('@/views/groups/EditGroup.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'mapPersonal',
        path: 'groups/map/:id/:userId',
        component: () => import('@/views/groups/GroupMap.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'map',
        path: 'groups/map/:id',
        component: () => import('@/views/groups/GroupMap.vue'),
        meta: { requiresAuth: true },
      },

      // CHECKLISTS
      {
        path: 'checklists',
        name: 'checklists',
        component: () => import('@/views/Checklists.vue'),
      },
      {
        path: 'checklists/showChecklistGroup/:id',
        name: 'checklistGroup',
        component: () => import('@/views/checklists/ChecklistGroup.vue'),
      },
      {
        path: 'checklists/showChecklist/:id',
        name: 'singleChecklist',
        component: () => import('@/views/checklists/SingleChecklist.vue'),
      },
      {
        name: 'editChecklist',
        path: 'checklists/edit/:id',
        component: () => import('@/views/checklists/EditChecklist.vue'),
      },

      // WARNINGS
      {
        path: 'warnings',
        name: 'warnings',
        component: () => import('@/views/Warnings.vue'),
      },
      {
        path: 'warnings/warning/:id',
        name: 'singleWarning',
        component: () => import('@/views/warnings/Warning.vue'),
      },          {
        path: 'warnings/security-warning/:id',
        name: 'singleSecurityWarning',
        component: () => import('@/views/warnings/SecurityWarning.vue'),
      },
    ],
  },

];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(async(to, from, next) => {
  const userStore = useUserStore();
  const { groupsHidden, checklistsHidden, warningsHidden, notificationSettingsHidden } = useFeatureVisibility();

  // Block direct navigation (deep links / programmatic nav) into a section the
  // app customization hides. Before the user is loaded nothing is hidden, so
  // this is a no-op then (consistent with "default to nothing hidden").
  const redirect = hiddenSectionRedirect(to.path, {
    groupsHidden: groupsHidden.value,
    checklistsHidden: checklistsHidden.value,
    warningsHidden: warningsHidden.value,
    notificationSettingsHidden: notificationSettingsHidden.value,
  });

  if (redirect && to.path !== redirect) {
    next(redirect);
    return;
  }

  // Only check authentication for routes that explicitly require it
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (userStore.currentUser) {
      next();
    } else {
      // No valid token or user data, redirect to locked screen
      next('/locked/');
    }
  } else {
    // Route doesn't require auth, allow access
    next();
  }
});

export default router;
